import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFF]">
      <Helmet>
        <title>Privacy Policy – How Cura Protects Your Health Data</title>
        <meta name="description" content="Learn how Cura safeguards your personal and medical information with encryption and strict privacy protocols. Your health data stays yours ✓" />
        <link rel="canonical" href="https://mycura.org/privacy" />
        <meta property="og:title" content="Privacy Policy – How Cura Protects Your Health Data" />
        <meta property="og:description" content="Learn how Cura safeguards your personal and medical information with encryption and strict privacy protocols." />
        <meta property="og:url" content="https://mycura.org/privacy" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy – How Cura Protects Your Health Data" />
        <meta name="twitter:description" content="Learn how Cura safeguards your personal and medical information with encryption and strict privacy protocols." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cura Privacy Policy",
            "url": "https://mycura.org/privacy",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
      </Helmet>
      {/* Back Button - Top Left Corner */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200 py-16 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold mb-4 text-gray-900">Privacy Policy</h1>
          <p className="text-xl text-gray-600">Your privacy and security are our top priorities</p>
        </motion.div>
      </div>

      {/* Content Section */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">


            <div className="space-y-12">
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  We are committed to protecting your privacy and ensuring the security of your personal and medical information.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI medical assistance service.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Information We Collect</h2>
                <p className="text-gray-700 text-lg mb-6">We collect the following types of information:</p>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Personal information (name, email address, phone number)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Medical information you share during conversations with our AI</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Usage data and interaction history</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Device and technical information</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Use Your Information</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">To provide medical information and assistance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">To improve our AI service and accuracy</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">To personalize your experience</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">To communicate with you about our services</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">To ensure compliance with healthcare regulations</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Security</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    We implement appropriate technical and organizational security measures to protect your information.
                    Our systems are designed to ensure security and maintain the confidentiality of your medical information.
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Information Sharing</h2>
                <p className="text-gray-700 text-lg mb-6">
                  We do not sell your personal information. We may share your information with:
                </p>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Healthcare providers (with your consent)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Service providers who assist in operating our platform</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Legal authorities when required by law</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Rights</h2>
                <p className="text-gray-700 text-lg mb-6">You have the right to:</p>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Access your personal information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Correct inaccurate information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Request deletion of your information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Opt-out of certain data sharing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mt-3 mr-4 flex-shrink-0"></span>
                      <span className="text-lg">Receive a copy of your information</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-700 text-lg mb-6">
                    If you have any questions about this Privacy Policy, please contact us:
                  </p>
                  <div className="space-y-4 text-gray-700">
                    <div className="flex items-center text-lg">
                      <span className="font-medium mr-2">Email:</span>
                      <a href="mailto:founders@mycura.org" className="text-blue-600 hover:text-blue-800 hover:underline">founders@mycura.org</a>
                    </div>
                    <div className="flex items-center text-lg">
                      <span className="font-medium mr-2">Phone:</span>
                      <a href="tel:+18665204146" className="text-blue-600 hover:text-blue-800 hover:underline">1-866-520-4146</a>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 